import React, {useContext, useEffect, useState} from 'react';

import {PlatformCell} from 'src/components/user-platform-column/platform-cell';
import {useUserStatuses, useUserTypes} from 'src/hooks/dictionaries';
import {useProducts} from 'src/hooks/dictionaries/useProducts';
import {useAllUserSeatAssignments} from 'src/modules/profile/user/hooks/use-user-seat-assignments';
import {formatLicenseDate} from 'src/modules/profile/utils/formatLicenseDate';
import {SeatAssignmentStatus} from 'src/openapi/admin/models/SeatAssignmentStatus';
import {useAppDispatch} from 'src/store/hooks';

import {FlatButton} from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {Message} from '@pb/kit-component-message';
import {Skeleton, SkeletonItem} from '@pb/kit-component-skeleton';
import {LocaleContext} from '@pb/kit-context-locale';

import {selectCurrentProductAssignments} from './access-summary';
import {getDrawerLicense} from './drawer-license-request';

import {accessCard, fieldsGrid, productYes} from './user-details-drawer.module.styl';

import type {LicenseShortInfo} from 'src/openapi/admin/models/LicenseShortInfo';
import type {UserDetails} from 'src/openapi/admin/models/UserDetails';

export const DrawerLoadError = ({message, retry}: {message: string, retry: () => void}) => (
    <Message color="error">
        {message} <FlatButton onClick={retry}>Retry</FlatButton>
    </Message>
);

export const AccessDates = ({start, end}: {start?: string, end?: string}) => {
    const {locale} = useContext(LocaleContext);
    return (
        <div className={fieldsGrid}>
            <div><Label>Start Date</Label><div>{start ? formatLicenseDate(locale.dateFormat, start) : '—'}</div></div>
            <div><Label>End Date</Label><div>{end ? formatLicenseDate(locale.dateFormat, end) : '—'}</div></div>
        </div>
    );
};

const PlatformDates = ({accountId}: {accountId: number}) => {
    const dispatch = useAppDispatch();
    const [state, setState] = useState<{
        loading: boolean, license?: LicenseShortInfo, error?: string,
    }>({loading: true});
    const [version, setVersion] = useState(0);
    useEffect(() => {
        const controller = new AbortController();
        setState({loading: true});
        const load = async () => {
            try {
                const response = await dispatch(getDrawerLicense(accountId, controller.signal));
                if (!response.payload?.success) {
                    throw response.payload?.data;
                }
                if (!controller.signal.aborted) {
                    setState({loading: false, license: response.payload.data});
                }
            } catch {
                if (!controller.signal.aborted) {
                    setState({loading: false, error: 'Unable to load Platform dates.'});
                }
            }
        };
        load();
        return () => controller.abort();
    }, [accountId, dispatch, version]);
    if (state.error) {
        return <DrawerLoadError message={state.error} retry={() => setVersion(value => value + 1)}/>;
    }
    return (
        <Skeleton shown={state.loading} view={<SkeletonItem height={40}/>}>
            <AccessDates start={state.license?.startDate} end={state.license?.endDate}/>
        </Skeleton>
    );
};

const ProductCards = ({userId}: {userId: number}) => {
    const {products, status, error: catalogueError, loadProducts} = useProducts();
    const {assignments, error, refresh} = useAllUserSeatAssignments(userId, SeatAssignmentStatus.ACTIVE);
    useEffect(() => {
        loadProducts();
    }, [loadProducts]);
    if (error || catalogueError) {
        return (
            <DrawerLoadError
                message="Unable to load product access."
                retry={() => {
                    refresh(); loadProducts();
                }}
            />
        );
    }
    // Subscription terms are date-only values; compare in UTC, as the service does.
    const current = selectCurrentProductAssignments(assignments || [], new Date().toISOString().slice(0, 10));
    return (
        <Skeleton shown={assignments === null || status !== 'succeeded'} view={<SkeletonItem height={100}/>}>
            {products.map(product => {
                const assignment = current.get(product.productExternalKey);
                if (!assignment) {
                    return null;
                }
                return (
                    <section className={accessCard} key={product.productExternalKey} aria-label={product.name}>
                        <Group justify="space-between">
                            <strong>{product.name}</strong>
                            <strong className={productYes}>Yes</strong>
                        </Group>
                        <AccessDates
                            start={assignment.subscriptionStartDate}
                            end={assignment.subscriptionEndDate}
                        />
                    </section>
                );
            })}
        </Skeleton>
    );
};

type Props = {
    userId: number,
    accountId?: number,
    details: UserDetails,
    canViewProducts: boolean,
    canViewLicense: boolean,
};

export const DrawerAccessSummary = ({userId, accountId, details, canViewProducts, canViewLicense}: Props) => {
    const {getUserTypeByCode} = useUserTypes();
    const {getUserStatusByCode} = useUserStatuses();
    return (
        <Group direction="v" gap="m">
            <section className={accessCard} aria-label="Platform">
                <Group justify="space-between">
                    <strong>Platform</strong>
                    <PlatformCell
                        showStatusMarker={false}
                        type={getUserTypeByCode(details.typeCode)?.description || details.typeCode}
                        status={getUserStatusByCode(details.statusCode)?.description || details.statusCode}
                        statusCode={details.statusCode}
                    />
                </Group>
                {details.statusCode !== 'EMPTY' && (canViewLicense && accountId ? (
                    <PlatformDates key={accountId} accountId={accountId}/>
                ) : <Message color="info">Platform dates are unavailable.</Message>)}
            </section>
            {canViewProducts ? <ProductCards userId={userId}/> :
                    <Message color="info">You do not have permission to view product access.</Message>}
        </Group>
    );
};
